import React, { useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, Row, Space, Table, Typography } from 'antd';
import CampaignRequestorAdGroupsDialog from './CampaignRequestorAdGroupsDialog';
import moment from 'moment';
import S3Image from '../CampaignLauncher/components/S3Image';
import { DeleteOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

export const CampaignRequestorAdGroup = ({
  form,
  index,
  fieldKey,
  restField,
  name,
  editable = false,
  setPreviousValues,
}) => {
  const campaigns = form?.getFieldValue('campaigns') || [];
  const campaignType = form?.getFieldValue('type'); // Get campaign type
  const isVideoType = campaignType === 8; // Video campaign type
  const adGroup = (campaigns[index] && campaigns[index].adGroup) || {};
  const adGroupCreatives = (adGroup && adGroup.adGroupCreatives) || [];
  const allMedias = adGroupCreatives.reduce((acc, item) => {
    return acc.concat(item.medias);
  }, []);

  const [showAdGroupsModal, setShowAdGroupsModal] = useState(false);

  const handleRemoveAdGroup = (record) => {
    const newAdGroupCreatives = adGroupCreatives.filter(
      (adGroupCreative) => adGroupCreative.name !== record.name
    );

    const updatedCampaigns = campaigns.map((campaign, i) => {
      if (i === index) {
        return {
          ...campaign,
          adGroup: {
            ...(campaign.adGroup || {}),
            adGroupCreatives: newAdGroupCreatives,
          },
        };
      }
      return campaign;
    });

    form?.setFieldsValue({
      campaigns: updatedCampaigns,
    });
    setPreviousValues && setPreviousValues({
      ...form?.getFieldsValue(),
      campaigns: updatedCampaigns,
    });
  };

  return (
    <>
      <Card title="AD GROUP">
        <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
          <div className="requester-table-header">
            <Row>
              <Col span={12}>
                <Title level={5}>DISPLAY AD GROUPS</Title>
              </Col>
              {editable && adGroupCreatives.length > 0 ? (
                <Col span={12}>
                  <div style={{ textAlign: 'end' }}>
                    <Button onClick={() => setShowAdGroupsModal(true)}>Edit Ad Groups</Button>
                  </div>
                </Col>
              ) : (
                <></>
              )}
            </Row>
          </div>
          {!adGroupCreatives.length ? (
            <div style={{ padding: 20 }}>
              <Space direction="vertical" size="small" align="center" style={{ width: '100%' }}>
                {editable ? (
                  <>
                    <Text>Select ad groups from your library to include their ad units</Text>
                    <Button
                      type="primary"
                      onClick={() => {
                        setShowAdGroupsModal(true);
                      }}
                    >
                      Select Ad Groups
                    </Button>
                  </>
                ) : (
                  <Text>No Ad Groups Selected</Text>
                )}
              </Space>
            </div>
          ) : (
            <>
              <Form.List
                name={[name, 'adGroup', 'adGroupCreatives']}
                fieldKey={[fieldKey, 'adGroup', 'adGroupCreatives']}
              >
                {(adGroupCreativesFields) => {
                  return (
                    <Table
                      key="name"
                      scroll={{ x: 800 }}
                      columns={[
                        {
                          title: 'Display Ad Group Name',
                          dataIndex: 'name',
                          key: 'name',
                          ellipsis: true,
                          width: 180,
                        },
                        {
                          title: isVideoType
                            ? 'Click-Through Link (Optional)'
                            : 'Click-Through Link',
                          dataIndex: 'clickthrough',
                          key: 'clickthrough',
                          ellipsis: true,
                          width: 150,
                          render: (clickthrough, record, index) => {
                            // In review mode, just display the text
                            if (!editable) {
                              return <Text>{record.clickthrough || '-'}</Text>;
                            }
                            // In edit mode, show the form input
                            return (
                              <Form.Item
                                {...adGroupCreativesFields[index]}
                                name={[index, 'clickthrough']}
                                fieldKey={[index, 'clickthrough']}
                                rules={[
                                  ...(isVideoType
                                    ? []
                                    : [
                                        {
                                          required: true,
                                          message: 'Click-through URL is required',
                                        },
                                      ]),
                                  {
                                    type: 'url',
                                    message: 'Please enter a valid URL',
                                  },
                                ]}
                              >
                                <Input disabled={!editable} />
                              </Form.Item>
                            );
                          },
                        },
                        {
                          title: 'Duration',
                          dataIndex: 'duration',
                          key: 'duration',
                          width: 260,
                          render: (_, record, index) => {
                            // In review mode, just display the dates as text
                            if (!editable) {
                              return (
                                <Space direction="vertical" size="small">
                                  <Text>Start: {record.startDate ? moment(record.startDate).format('MM/DD/YYYY') : '-'}</Text>
                                  <Text>End: {record.endDate ? moment(record.endDate).format('MM/DD/YYYY') : '-'}</Text>
                                </Space>
                              );
                            }
                            // In edit mode, show the form inputs
                            return (
                              <Row gutter={8}>
                                <Col xs={24} sm={12}>
                                  <Space
                                    direction="vertical"
                                    size="small"
                                    style={{ width: '100%', minWidth: '120px' }}
                                  >
                                    <Text>Start Date</Text>
                                    <Form.Item
                                      {...adGroupCreativesFields[index]}
                                      name={[index, 'startDate']}
                                      fieldKey={[index, 'startDate']}
                                    >
                                      <DatePicker disabled={!editable} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Space>
                                </Col>
                                <Col xs={24} sm={12}>
                                  <Space
                                    direction="vertical"
                                    size="small"
                                    style={{ width: '100%', minWidth: '120px' }}
                                  >
                                    <Text>End Date</Text>
                                    <Form.Item
                                      {...adGroupCreativesFields[index]}
                                      name={[index, 'endDate']}
                                      fieldKey={[index, 'endDate']}
                                    >
                                      <DatePicker disabled={!editable} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Space>
                                </Col>
                              </Row>
                            );
                          },
                        },
                        {
                          title: 'Gallery',
                          dataIndex: 'medias',
                          key: 'medias',
                          width: 120,
                          render: (medias) => {
                            return (
                              <Space size="small">
                                {medias.map((media, index) => (
                                  <S3Image key={index} imgKey={media.key} />
                                ))}
                              </Space>
                            );
                          },
                        },
                        {
                          title: '',
                          dataIndex: 'actions',
                          key: 'actions',
                          width: 60,
                          render: (_, record) => {
                            if (!editable) return null;
                            return (
                              <Space size="small" align="end" style={{ width: '100%' }}>
                                <Button
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  danger
                                  onClick={() => handleRemoveAdGroup(record)}
                                ></Button>
                              </Space>
                            );
                          },
                        },
                      ]}
                      dataSource={adGroupCreatives}
                      pagination={false}
                      className="requester-table"
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2}>
                            {adGroupCreatives.length} Display Ad Group(s)
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} colSpan={2}>
                            {allMedias.length} Display Ad Unit(s)
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  );
                }}
              </Form.List>
            </>
          )}
        </div>

        {/* Create hidden input for adGroupCreatives*/}
        <Form.Item
          {...restField}
          name={[name, 'adGroup', 'adGroupCreatives']}
          fieldKey={[fieldKey, 'adGroup', 'adGroupCreatives']}
          hidden
          rules={[
            {
              required: true,
              message: `Please select ad groups for ${campaigns[index]?.name || 'this line item'}`,
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Card>
      {showAdGroupsModal && (
        <CampaignRequestorAdGroupsDialog
          visible={showAdGroupsModal}
          setVisible={setShowAdGroupsModal}
          orgId={form?.getFieldValue('advertiserId')}
          selectedAdGroupCreatives={adGroupCreatives}
          onAddSelections={(creatives) => {
            const updatedCampaigns = campaigns.map((campaign, i) => {
              if (i === index) {
                return {
                  ...campaign,
                  adGroup: {
                    ...(campaign.adGroup || {}),
                    adGroupCreatives: creatives.map((creative) => {
                      // Check if creative already exists in the list
                      const existingAudience = adGroupCreatives.find(
                        (a) => a.name === creative.name
                      );
                      const adGroup = campaign.adGroup;

                      if (existingAudience) {
                        return existingAudience;
                      }

                      delete creative.id;
                      return {
                        ...creative,
                        startDate:
                          adGroup && adGroup.startDate
                            ? moment(campaign.adGroup?.startDate)
                            : moment(form?.getFieldValue('startDate')),
                        endDate:
                          adGroup && adGroup.endDate
                            ? moment(campaign.adGroup?.endDate)
                            : moment(form?.getFieldValue('endDate')),
                      };
                    }),
                  },
                };
              }
              return campaign;
            });
            form?.setFieldsValue({
              campaigns: updatedCampaigns,
            });
            setPreviousValues && setPreviousValues({
              ...form?.getFieldsValue(),
              campaigns: updatedCampaigns,
            });

            // Trigger validation for clickthrough fields
            setTimeout(() => {
              creatives.forEach((_, idx) => {
                form?.validateFields([
                  ['campaigns', index, 'adGroup', 'adGroupCreatives', idx, 'clickthrough']
                ]).catch(() => {});
              });
            }, 0);
          }}
        />
      )}
    </>
  );
};
